/**
 * Orion API — TypeScript types matching the wire contracts in docs/api-contracts.md.
 *
 * Written by hand from the contracts doc. All string-typed date fields stay
 * strings here; formatting is the view layer's job.
 *
 * Naming mirrors the JSON keys the API returns, not the DB column names.
 *
 * Phase 3 Stage 13 replaced the single-column status lifecycle with the
 * six-stage funnel. `statusCode` / `statusName` no longer exist anywhere on
 * the wire; `stageCode` / `stageName` plus the derived `lifecycle` took
 * their place.
 */

// ---------------------------------------------------------------------------
// Shared envelope types
// ---------------------------------------------------------------------------

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface ListResponse<T> {
  data: T[]
}

export interface ApiErrorBody {
  code: string
  message: string
  details?: unknown
  traceId: string
}

// ---------------------------------------------------------------------------
// Lookup types — small reference collections used by pickers, filters and
// the board. `lookup-users` is paginated; the rest return `{ data: [...] }`.
// ---------------------------------------------------------------------------

/** `PreCommitment` | `Active` | `Terminal`. */
export type StageBand = 'PreCommitment' | 'Active' | 'Terminal'

export interface LookupStage {
  stageCode: string
  stageName: string
  stageBand: StageBand
  /** Advisory time-box in days. Null for stages with no box. */
  defaultDurationDays: number | null
  sortOrder: number
}

export interface LookupBusinessUnit {
  /** Finance code — the natural key and the wire identifier. */
  businessUnitCode: string
  /** Short label (`Boots`, `M&I`). Never used as a key. */
  displayCode: string
  businessUnitName: string
  sortOrder: number
}

export interface LookupBlockedReason {
  blockedReasonCode: string
  blockedReasonName: string
  sortOrder: number
}

export interface LookupAiActClass {
  aiActClassCode: string
  aiActClassName: string
  sortOrder: number
}

export interface LookupDomain {
  domainId: number
  domainName: string
}

export interface LookupCapability {
  capabilityId: number
  domainId: number
  parentCapabilityId: number | null
  capabilityName: string
  capabilityLevel: number
}

export interface LookupUser {
  entraObjectId: string
  displayName: string
  userPrincipalName: string
}

// ---------------------------------------------------------------------------
// Domain / capability — unchanged by Stage 13.
// ---------------------------------------------------------------------------

export interface Domain {
  domainId: number
  domainName: string
  domainDescription: string | null
  sortOrder: number
  isActive: boolean
  modifiedAt: string
  capabilityCount: number
  initiativeCount: number
}

export interface Capability {
  capabilityId: number
  domainId: number
  domainName: string
  parentCapabilityId: number | null
  capabilityName: string
  capabilityDescription: string | null
  capabilityLevel: number
  sortOrder: number
  isActive: boolean
  modifiedAt: string
  initiativeCount: number
  children?: Capability[]
  parent?: Capability
}

// ---------------------------------------------------------------------------
// Initiative
// ---------------------------------------------------------------------------

/**
 * Derived server-side on every read, never stored:
 * closed beats blocked beats active.
 */
export type Lifecycle = 'active' | 'blocked' | 'closed'

export interface InitiativeListItem {
  initiativeId: number
  initiativeName: string

  stageCode: string
  stageName: string
  stageBand: StageBand
  lifecycle: Lifecycle

  businessUnitCode: string
  businessUnitDisplayCode: string
  businessUnitName: string

  capabilityId: number
  capabilityName: string
  domainId: number
  domainName: string

  ownerObjectId: string
  ownerDisplayName: string

  blockedReasonCode: string | null
  blockedReasonName: string | null

  closureReason: string | null
  closedAt: string | null

  targetDate: string | null
  /** ISO timestamp of the gate clear that landed it here. Null at Idea. */
  stageEnteredAt: string | null
  /** Advisory. Null unless the stage carries a default duration. */
  stageDueDate: string | null
  stageOverdue: boolean

  modifiedAt: string
}

// ---------------------------------------------------------------------------
// Activity feed — four entry kinds in one stream, newest first.
// ---------------------------------------------------------------------------

export interface FieldChange {
  field: string
  label: string
  before: string | null
  after: string | null
}

interface ActivityBase {
  at: string
  actorObjectId: string
  actorDisplayName: string
}

export interface ChangeActivity extends ActivityBase {
  kind: 'change'
  isCreate: boolean
  changes: FieldChange[]
}

export interface GateActivity extends ActivityBase {
  kind: 'gate'
  gateCode: string
  checklistVersion: string
  outcome: GateOutcome
  fromStageCode: string
  toStageCode: string
  reason: string | null
  overrideReason: string | null
  overrideApproverObjectId: string | null
  overrideApproverDisplayName: string | null
}

export interface EvidenceActivity extends ActivityBase {
  kind: 'evidence'
  evidenceId: number
  evidenceKind: string
  title: string
  evidenceUrl: string | null
  stageCode: string
}

export interface CommentActivity extends ActivityBase {
  kind: 'comment'
  commentId: number
  body: string
  stageCode: string
  edited: boolean
}

export type ActivityEntry = ChangeActivity | GateActivity | EvidenceActivity | CommentActivity

export interface InitiativeGateSummary {
  nextGateCode: string | null
  nextGateName: string | null
  ready: boolean
  unmetHardCriteria: string[]
  overrideCount: number
}

export interface Initiative extends InitiativeListItem {
  initiativeDescription: string

  blockedNote: string | null
  closureNote: string | null

  successMeasure: string | null
  baseline: string | null
  baselineDeferred: boolean
  dataRequirements: string | null
  aiActClassCode: string | null
  aiActClassName: string | null
  governanceNote: string | null
  governanceNoteConfirmedAt: string | null

  trackerUrl: string | null
  pilotCohort: string | null
  disclosureText: string | null

  result: string | null
  costEnvelope: string | null
  runOwnerConfirmed: boolean

  monitoringUrl: string | null
  runbookUrl: string | null

  forkedFromInitiativeId: number | null
  forkedFromInitiativeName: string | null

  isActive: boolean

  createdByObjectId: string
  createdByDisplayName: string
  modifiedByObjectId: string
  modifiedByDisplayName: string

  gateSummary: InitiativeGateSummary
  activity: ActivityEntry[]
}

// ---------------------------------------------------------------------------
// Gates
// ---------------------------------------------------------------------------

export type GateCode = 'G0' | 'G1' | 'G2' | 'G3' | 'G4'

export type CriterionKind = 'Hard' | 'Attest' | 'Advisory'

export type GateOutcome = 'Advance' | 'Park' | 'Salvage' | 'Close' | 'StepBack'

export interface RecordedCriterionResult {
  met: boolean
  overridden: boolean
  note: string | null
  clearedAt: string
}

export interface GateCriterionReadiness {
  criterionKey: string
  criterionText: string
  kind: CriterionKind
  met: boolean
  overridable: boolean
  attestationRequired: boolean
  advisory: boolean
  recorded: RecordedCriterionResult | null
}

export interface GateLastClear {
  outcome: GateOutcome
  clearedAt: string
  clearedByObjectId: string
  clearedByDisplayName: string
  reason: string | null
  overridden: boolean
}

/**
 * Why an assessed gate is where it is. Mirrors `GateAssessmentState` in
 * api/src/shared/domain/gateEngine.ts.
 *
 * Without this, three unmet criteria on an automated gate render
 * identically whether the model scored the idea poorly, has not run yet, or
 * crashed — and those need three different things from the reader.
 */
export type GateAssessmentState = 'Pending' | 'Stale' | 'Failed' | 'Complete'

export type DuplicateState = 'Pending' | 'Clear' | 'NeedsReview'

export interface GateAssessment {
  state: GateAssessmentState
  assessedAt: string | null
  problemClarityScore: number | null
  aiPlausibilityScore: number | null
  duplicateTopScore: number | null
  duplicateTopInitiativeId: number | null
  duplicateState: DuplicateState
  rationale: string | null
  failureReason: string | null
  modelDeployment: string | null
  promptVersion: string | null
  /** Sent by the API so the panel never carries its own copy. */
  thresholds: {
    problemClarityMin: number
    aiPlausibilityMin: number
    duplicateMax: number
  }
}

export interface GateReadiness {
  gateCode: GateCode
  gateName: string
  checklistVersion: string
  fromStageCode: string
  toStageCode: string
  isCurrentGate: boolean
  isPassed: boolean
  ready: boolean
  unmetHardCriteria: string[]
  authorised: boolean
  authorityNote: string
  criteria: GateCriterionReadiness[]
  lastClear: GateLastClear | null
  /** Null on gates whose checklist is not assessment-backed. */
  assessment: GateAssessment | null
}

export interface GateReadinessResponse {
  initiativeId: number
  currentStageCode: string
  overrideCount: number
  data: GateReadiness[]
}

export interface ClearGateResponse {
  gateClearId: number
  gateCode: GateCode
  checklistVersion: string
  outcome: GateOutcome
  fromStageCode: string
  toStageCode: string
  initiative: Initiative
}

// ---------------------------------------------------------------------------
// Comments, evidence, team
// ---------------------------------------------------------------------------

export interface Comment {
  commentId: number
  initiativeId: number
  body: string
  stageCode: string
  stageName: string
  authorObjectId: string
  authorDisplayName: string
  createdAt: string
  modifiedAt: string
  edited: boolean
}

export const EVIDENCE_KINDS = [
  'TestResult',
  'Measurement',
  'FailureMode',
  'ComplianceReview',
  'Demo',
  'Other',
] as const

export type EvidenceKind = (typeof EVIDENCE_KINDS)[number]

export interface Evidence {
  evidenceId: number
  initiativeId: number
  evidenceKind: EvidenceKind
  title: string
  evidenceUrl: string | null
  note: string | null
  stageCode: string
  stageName: string
  recordedByObjectId: string
  recordedByDisplayName: string
  recordedAt: string
  modifiedAt: string
}

export interface TeamMember {
  memberId: number
  initiativeId: number
  entraObjectId: string
  displayName: string
  userPrincipalName: string
  roleNote: string | null
  addedByObjectId: string
  addedByDisplayName: string
  modifiedAt: string
}

// ---------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------

export type StatsGroupBy = 'stage' | 'blockedReason' | 'businessUnit'

export interface StatsGroup {
  key: string
  name: string
  count: number
  active: number
  blocked: number
  closed: number
  overrides: number
}

export interface Stats {
  groupBy: StatsGroupBy
  total: number
  lifecycle: { active: number; blocked: number; closed: number }
  overrides: number
  groups: StatsGroup[]
}

// ---------------------------------------------------------------------------
// App navigation
//
// `home` and `domains` are navigation targets without a nav link: the
// header's mark routes to home, and domains is reached by cross-link. While
// the user is on either, no tab reads as active.
// ---------------------------------------------------------------------------

export type NavTab = 'home' | 'chat' | 'initiatives' | 'domains' | 'capabilities'
